import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import NavBarComponent from "../components/navBar/NavBarComponent.js";
import FooterComponent from "../components/home/FooterComponent.js";
import { sendSlackMessage } from "../models/notifications";

// ─── tokens ──────────────────────────────────────────────────────────────────

const T = {
  pageBg:        "#08080a",
  cardBg:        "#111114",
  cardBgAlt:     "#0d0d10",
  border:        "rgba(255,255,255,0.06)",
  accent:        "#c9a85c",
  accentDim:     "rgba(201,168,92,0.10)",
  accentBorder:  "rgba(201,168,92,0.28)",
  textPrimary:   "#f5f0e8",
  textSecondary: "#8c8880",
  textMuted:     "#4a4642",
  radius:        "20px",
  radiusSm:      "14px",
  font:          "'DM Sans', 'Geist', system-ui, sans-serif",
};

const EASE     = [0.22, 1, 0.36, 1];
const DURATION = 0.5;

// ─── data ─────────────────────────────────────────────────────────────────────

const LABOUR = [
  { work: "Integration and migration",     owner: "Pabloo" },
  { work: "Rate and rule design",          owner: "Pabloo" },
  { work: "Campaign build",               owner: "Pabloo" },
  { work: "Weekly issuance review",        owner: "Pabloo" },
  { work: "Dormancy and float monitoring", owner: "Pabloo" },
  { work: "Quarterly strategy call",       owner: "Pabloo" },
  { work: "Economy Review document",       owner: "Pabloo" },
  { work: "Stack changes and replatforms", owner: "Pabloo" },
];

const INCLUSIONS = [
  { meter: "Dedicated Economist",          value: "Yes" },
  { meter: "Integration Engineer",         value: "Yes" },
  { meter: "Economist hours per month",    value: "4" },
  { meter: "Support response",             value: "Inside 1 hour" },
  { meter: "Weekly issuance review",       value: "Yes" },
  { meter: "Economy Review document",      value: "Each quarter" },
  { meter: "Strategy call",                value: "One each quarter" },
  { meter: "Customer accounts",            value: "200,000+" },
  { meter: "Store accounts",               value: "20+" },
  { meter: "Team accounts",                value: "Unlimited" },
  { meter: "Credit events each month",     value: "Unlimited" },
  { meter: "Credit issued",                value: "Unlimited" },
  { meter: "API access",                   value: "Full" },
];

const STEPS = [
  {
    n: "01",
    title: "One call",
    body: "You meet your Economist. They learn your catalog, your margins, and your customer shape.",
  },
  {
    n: "02",
    title: "Integration",
    body: "Your integration engineer handles setup and migration. Your team does not need to be involved.",
  },
  {
    n: "03",
    title: "The economy runs",
    body: "Rates are set, campaigns are built, and the weekly review cycle starts. You get the Economy Review each quarter.",
  },
];

// ─── sub-components ───────────────────────────────────────────────────────────

function Divider() {
  return (
    <div style={{ height: "1px", background: T.border, width: "100%" }} />
  );
}

function SectionWrap({ children, style }) {
  return (
    <div
      style={{
        maxWidth: "860px",
        margin: "0 auto",
        padding: "72px 24px",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <p
      className="text-xs font-bold uppercase tracking-widest mb-5"
      style={{ color: T.accent, letterSpacing: "0.12em", fontFamily: T.font }}
    >
      {children}
    </p>
  );
}

function SectionHeading({ children }) {
  return (
    <h2
      className="font-semibold mb-8"
      style={{ color: T.textPrimary, fontSize: "1.25rem", fontFamily: T.font }}
    >
      {children}
    </h2>
  );
}

// ─── main page ────────────────────────────────────────────────────────────────

export default function ManagedPage() {
  const navigate = useNavigate();

  function handleCTA() {
    sendSlackMessage("Managed Economy page: 'Talk to an economist' clicked.", "#website-updates");
    navigate("/bookCall");
  }

  return (
    <>
      <Helmet>
        <title>Managed Economy — Pabloo</title>
        <meta
          name="description"
          content="A dedicated Economist and integration engineer run your store credit economy end to end. Starting at $3,300/month."
        />
      </Helmet>

      <div style={{ background: T.pageBg, minHeight: "100vh" }}>
        <NavBarComponent theme="managed" />

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: DURATION, ease: EASE }}
        >

          {/* ── Hero ─────────────────────────────────────────────────────── */}
          <SectionWrap style={{ paddingTop: "120px" }}>
            <SectionLabel>Pabloo Managed Economy</SectionLabel>

            <h1
              className="font-bold mb-6"
              style={{
                color: T.textPrimary,
                fontSize: "clamp(2.75rem, 6vw, 4.25rem)",
                lineHeight: 1.08,
                letterSpacing: "-0.04em",
                fontFamily: T.font,
              }}
            >
              Your credit economy.<br />Fully managed.
            </h1>

            <p
              className="mb-10"
              style={{
                color: T.textSecondary,
                fontSize: "1.0625rem",
                lineHeight: 1.7,
                maxWidth: "540px",
                fontFamily: T.font,
              }}
            >
                A dedicated Economist and an integration engineer build your brand's economy. Rates, campaigns, weekly
                reviews, and the quarterly Economy Review, run for you. You focus on the brand.
            </p>

            <div className="flex flex-col sm:flex-row items-start gap-4 mb-10">
              <div
                className="inline-flex items-baseline gap-2"
                style={{
                  background: T.accentDim,
                  border: `1px solid ${T.accentBorder}`,
                  borderRadius: T.radiusSm,
                  padding: "14px 24px",
                  boxShadow: `0 0 40px rgba(201,168,92,0.08)`,
                }}
              >
                <span
                  style={{
                    color: T.accent,
                    fontSize: "2.25rem",
                    fontWeight: 700,
                    fontFamily: T.font,
                    letterSpacing: "-0.02em",
                  }}
                >
                  $3,300
                </span>
                <span style={{ color: T.textMuted, fontSize: "0.9375rem", fontFamily: T.font }}>
                  / month baseline
                </span>
              </div>
            </div>

            <button
              type="button"
              onClick={handleCTA}
              style={{
                background: T.accent,
                color: "#08080a",
                border: "none",
                borderRadius: "99px",
                padding: "16px 44px",
                fontSize: "0.9375rem",
                fontWeight: 700,
                cursor: "pointer",
                fontFamily: T.font,
                letterSpacing: "0.01em",
                boxShadow: `0 0 32px rgba(201,168,92,0.22)`,
                transition: "opacity 0.15s, box-shadow 0.15s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.opacity = "0.9";
                e.currentTarget.style.boxShadow = "0 0 48px rgba(201,168,92,0.35)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = "1";
                e.currentTarget.style.boxShadow = "0 0 32px rgba(201,168,92,0.22)";
              }}
            >
              Talk to an economist
            </button>
          </SectionWrap>

          <Divider />

          {/* ── Who does what ────────────────────────────────────────────── */}
          <SectionWrap>
            <SectionLabel>Labour split</SectionLabel>
            <SectionHeading>Every line item is Pabloo.</SectionHeading>

            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontFamily: T.font,
                }}
              >
                <thead>
                  <tr>
                    {["Work", "Owner"].map((h) => (
                      <th
                        key={h}
                        style={{
                          textAlign: h === "Work" ? "left" : "center",
                          padding: "10px 16px",
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          letterSpacing: "0.08em",
                          textTransform: "uppercase",
                          color: T.textMuted,
                          borderBottom: `1px solid ${T.border}`,
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {LABOUR.map((row, i) => (
                    <tr
                      key={i}
                      style={{
                        borderBottom: i < LABOUR.length - 1 ? `1px solid ${T.border}` : "none",
                      }}
                    >
                      <td
                        style={{
                          padding: "14px 16px",
                          fontSize: "0.9375rem",
                          color: T.textSecondary,
                        }}
                      >
                        {row.work}
                      </td>
                      <td style={{ textAlign: "center", padding: "14px 16px" }}>
                        <span
                          style={{
                            display: "inline-block",
                            background: T.accentDim,
                            color: T.accent,
                            fontSize: "0.8125rem",
                            fontWeight: 600,
                            padding: "3px 12px",
                            borderRadius: "99px",
                          }}
                        >
                          Pabloo
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </SectionWrap>

          <Divider />

          {/* ── What is included ─────────────────────────────────────────── */}
          <SectionWrap>
            <SectionLabel>Plan details</SectionLabel>
            <SectionHeading>What is included</SectionHeading>

            <div
              style={{
                background: T.cardBg,
                border: `1px solid ${T.border}`,
                borderRadius: T.radius,
                overflow: "hidden",
                marginBottom: "20px",
              }}
            >
              {INCLUSIONS.map((row, i, arr) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "14px 24px",
                    borderBottom: i < arr.length - 1 ? `1px solid ${T.border}` : "none",
                    fontFamily: T.font,
                  }}
                >
                  <span style={{ color: T.textSecondary, fontSize: "0.9375rem" }}>
                    {row.meter}
                  </span>
                  <span style={{ color: T.textPrimary, fontSize: "0.9375rem", fontWeight: 500 }}>
                    {row.value}
                  </span>
                </div>
              ))}
            </div>

            <p className="text-sm leading-relaxed mb-3" style={{ color: T.textMuted, fontFamily: T.font }}>
              A customer account is one shopper with a credit balance above zero.
              Wallets that empty out stop counting on your next bill.
            </p>
            <p className="text-sm leading-relaxed" style={{ color: T.textMuted, fontFamily: T.font }}>
              Credit issued is never metered. Issue ten dollars or ten million.
              The price does not change.
            </p>
          </SectionWrap>

          <Divider />

          {/* ── How it starts ────────────────────────────────────────────── */}
          <SectionWrap>
            <SectionLabel>Process</SectionLabel>
            <SectionHeading>How it starts</SectionHeading>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {STEPS.map((step) => (
                <div
                  key={step.n}
                  style={{
                    background: T.cardBg,
                    border: `1px solid ${T.border}`,
                    borderLeft: `2px solid ${T.accent}`,
                    borderRadius: T.radius,
                    padding: "32px",
                  }}
                >
                  <p
                    style={{
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      letterSpacing: "0.1em",
                      color: T.textMuted,
                      marginBottom: "12px",
                      fontFamily: T.font,
                    }}
                  >
                    {step.n}
                  </p>
                  <p
                    className="font-semibold mb-3"
                    style={{ color: T.textPrimary, fontSize: "1rem", fontFamily: T.font }}
                  >
                    {step.title}
                  </p>
                  <p
                    className="text-sm leading-relaxed"
                    style={{ color: T.textSecondary, fontFamily: T.font }}
                  >
                    {step.body}
                  </p>
                </div>
              ))}
            </div>

            <p
              className="text-sm text-center mt-6"
              style={{ color: T.textMuted, fontStyle: "italic", fontFamily: T.font }}
            >
              Integration starts inside 5 business days of signing.
            </p>
          </SectionWrap>

          <Divider />

          {/* ── Guided vs Managed ────────────────────────────────────────── */}
          <SectionWrap>
            <SectionLabel>Comparison</SectionLabel>
            <SectionHeading>Not sure which tier fits?</SectionHeading>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div
                style={{
                  background: T.cardBgAlt,
                  border: `1px solid ${T.border}`,
                  borderRadius: T.radius,
                  padding: "28px",
                }}
              >
                <p
                  className="text-xs font-bold uppercase tracking-widest mb-3"
                  style={{ color: T.textMuted, letterSpacing: "0.1em", fontFamily: T.font }}
                >
                  Guided Economy
                </p>
                <p
                  className="font-bold mb-1"
                  style={{ color: T.textPrimary, fontSize: "1.25rem", fontFamily: T.font }}
                >
                  $900 / month
                </p>
                <p className="text-sm font-medium mb-4" style={{ color: T.textMuted, fontFamily: T.font }}>
                  You run it
                </p>
                <p className="text-sm leading-relaxed" style={{ color: T.textSecondary, fontFamily: T.font }}>
                  You set your own rates and run your own campaigns. Pabloo
                  answers inside 4 hours and runs one strategy call each quarter.
                </p>
              </div>

              <div
                style={{
                  background: T.cardBg,
                  border: `1px solid ${T.accentBorder}`,
                  borderRadius: T.radius,
                  padding: "28px",
                }}
              >
                <p
                  className="text-xs font-bold uppercase tracking-widest mb-3"
                  style={{ color: T.accent, letterSpacing: "0.1em", fontFamily: T.font }}
                >
                  Managed Economy
                </p>
                <p
                  className="font-bold mb-1"
                  style={{ color: T.textPrimary, fontSize: "1.25rem", fontFamily: T.font }}
                >
                  $3,300 / month
                </p>
                <p className="text-sm font-medium mb-4" style={{ color: T.accent, fontFamily: T.font }}>
                  Pabloo runs it
                </p>
                <p className="text-sm leading-relaxed" style={{ color: T.textSecondary, fontFamily: T.font }}>
                  A dedicated Economist and integration engineer handle
                  everything. You get the Economy Review each quarter and a
                  working economy from day one.
                </p>
              </div>
            </div>

            <p
              className="text-sm text-center"
              style={{ color: T.textMuted, fontStyle: "italic", fontFamily: T.font }}
            >
              The platform is identical. You are choosing who does the work.
            </p>
          </SectionWrap>

          <Divider />

          {/* ── CTA ──────────────────────────────────────────────────────── */}
          <SectionWrap style={{ paddingBottom: "96px" }}>
            <div className="flex flex-col items-center text-center gap-4">
              <h2
                className="font-bold"
                style={{
                  color: T.textPrimary,
                  fontSize: "clamp(1.5rem, 4vw, 2.25rem)",
                  lineHeight: 1.2,
                  letterSpacing: "-0.02em",
                  fontFamily: T.font,
                  maxWidth: "480px",
                }}
              >
                Ready to hand it off?
              </h2>
              <p
                style={{
                  color: T.textSecondary,
                  fontSize: "1rem",
                  lineHeight: 1.7,
                  maxWidth: "420px",
                  fontFamily: T.font,
                }}
              >
                Book a call with an Economist. They will walk through your
                store, your numbers, and what a working economy looks like for
                your brand.
              </p>
              <button
                type="button"
                onClick={handleCTA}
                style={{
                  background: T.accent,
                  color: "#08080a",
                  border: "none",
                  borderRadius: "99px",
                  padding: "16px 48px",
                  fontSize: "0.9375rem",
                  fontWeight: 700,
                  cursor: "pointer",
                  fontFamily: T.font,
                  letterSpacing: "0.01em",
                  boxShadow: `0 0 32px rgba(201,168,92,0.22)`,
                  transition: "opacity 0.15s, box-shadow 0.15s",
                  marginTop: "8px",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = "0.9";
                  e.currentTarget.style.boxShadow = "0 0 48px rgba(201,168,92,0.35)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = "1";
                  e.currentTarget.style.boxShadow = "0 0 32px rgba(201,168,92,0.22)";
                }}
              >
                Talk to an economist
              </button>
              <p className="text-xs" style={{ color: T.textMuted, fontFamily: T.font }}>
                No commitment. Integration starts inside 5 business days of signing.
              </p>
            </div>
          </SectionWrap>

        </motion.div>

        <FooterComponent />
      </div>
    </>
  );
}
